// src/components/dashboard/student/StudentCourses.tsx
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Clock,
  ArrowRight,
  BookOpen,
  Loader2,
  ChevronRight,
  Calendar,
  CheckCircle,
  Award,
  Shield,
  FileCheck,
} from "lucide-react";
import {
  enrollmentApi,
  certificateExamApi,
  certificateApi,
} from "../../../lib";
import { toast } from "sonner";

interface EnrollmentCourse {
  _id: string;
  title: string;
  thumbnail: string;
  shortDescription?: string;
  description?: string;
  difficulty?: string;
  rating?: number;
  price?: number;
  discount?: number;
  finalPrice?: number;
  currency?: string;
  instructorName?: string;
  instructorAvatar?: string;
}

interface Enrollment {
  _id: string;
  user: string;
  course: EnrollmentCourse | null;
  status: "active" | "completed" | "paused" | "cancelled";
  progress: number;
  completedLessons: string[];
  completedModules: string[];
  completedQuizzes: string[];
  certificateIssued?: boolean;
  certificateExamPassed?: boolean;
  certificateExamScore?: number;
  certificateExamAttempts?: number;
  lastAccessedLesson?: string;
  enrolledAt: string;
  completedAt?: string;
  expiresAt?: string;
}

interface ExamStatus {
  hasCertificateExam: boolean;
  examPassed: boolean;
  examAttempts: number;
  examAttemptsAllowed?: number;
  certificateIssued?: boolean;
  passingScore?: number;
  examId?: string;
}

const StudentCourses = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [examStatusMap, setExamStatusMap] = useState<
    Record<string, ExamStatus>
  >({});
  const [certificateMap, setCertificateMap] = useState<Record<string, boolean>>(
    {},
  );

  const loadExamStatuses = async (items: Enrollment[]) => {
    const map: Record<string, ExamStatus> = {};
    await Promise.all(
      items.map(async (item) => {
        const courseId = item.course?._id;
        if (!courseId) return;
        try {
          // Check if course has a certificate exam
          const examRes =
            await certificateExamApi.getCertificateExamByCourse(courseId);
          if (examRes?.success && examRes.data) {
            const exam = examRes.data;

            // Try to get exam result for this student (if taken)
            let examResult = null;
            try {
              const resultRes =
                await certificateExamApi.getCertificateExamResult(exam._id);
              if (resultRes?.success && resultRes.data) {
                examResult = resultRes.data.result;
              }
            } catch {
              // No exam taken yet
            }

            map[courseId] = {
              hasCertificateExam: true,
              examPassed: examResult?.passed || false,
              examAttempts: examResult?.attempts || 0,
              examAttemptsAllowed: exam.attemptsAllowed || 3,
              certificateIssued: false,
              passingScore: exam.passingScore || 70,
              examId: exam._id,
            };
          } else {
            map[courseId] = {
              hasCertificateExam: false,
              examPassed: false,
              examAttempts: 0,
              examAttemptsAllowed: 3,
              certificateIssued: false,
            };
          }
        } catch (err) {
          console.error(
            `Failed to load exam status for course ${courseId}:`,
            err,
          );
          map[courseId] = {
            hasCertificateExam: false,
            examPassed: false,
            examAttempts: 0,
            examAttemptsAllowed: 3,
            certificateIssued: false,
          };
        }
      }),
    );
    setExamStatusMap(map);
  };

  const loadCertificates = async (items: Enrollment[]) => {
    const map: Record<string, boolean> = {};
    await Promise.all(
      items.map(async (item) => {
        const courseId = item.course?._id;
        if (!courseId) return;
        try {
          const certRes = await certificateApi.getCertificateByCourse(courseId);
          if (certRes?.success && certRes.data) {
            map[courseId] = true;
          }
        } catch {
          map[courseId] = false;
        }
      }),
    );
    setCertificateMap(map);
  };

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        setLoading(true);
        const response = await enrollmentApi.getUserEnrollments({
          page: 1,
          limit: 50,
        });

        if (response && response.success) {
          const items: Enrollment[] = response.data || [];
          setEnrollments(items);
          await Promise.all([loadExamStatuses(items), loadCertificates(items)]);
        } else {
          setError(response?.message || "Failed to load enrollments");
        }
      } catch (err: any) {
        console.error("Enrollment fetch error:", err);
        setError(err.message || "Failed to load enrollments");
        toast.error("Failed to load your courses");
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, []);

  const handleTakeCertificateExam = (courseId: string, examId?: string) => {
    if (courseId) {
      navigate(`/course/${courseId}/certificate-exam`);
    }
  };

  const handleViewCertificate = (courseId: string) => {
    if (courseId) {
      navigate(`/certificate?courseId=${courseId}`);
    }
  };

  const handleContinue = (courseId: string) => {
    if (courseId) {
      navigate(`/course/${courseId}/learn`);
    }
  };

  // Helper function to check if exam should be shown (not taken yet, course completed)
  const shouldShowExamButton = (enrollment: Enrollment): boolean => {
    const courseId = enrollment.course?._id;
    if (!courseId) return false;

    const examStatus = examStatusMap[courseId];
    if (!examStatus) return false;

    // Check if course has certificate exam
    if (!examStatus.hasCertificateExam) return false;

    // Check if exam already passed
    if (examStatus.examPassed) return false;

    // Check if certificate already exists
    if (certificateMap[courseId]) return false;

    // Check if course is completed (progress >= 100 OR status === "completed")
    const isCompleted =
      enrollment.progress >= 100 || enrollment.status === "completed";
    if (!isCompleted) return false;

    // Check if user has attempts left
    const attemptsUsed = examStatus.examAttempts || 0;
    const attemptsAllowed = examStatus.examAttemptsAllowed || 3;
    if (attemptsUsed >= attemptsAllowed) return false;

    return true;
  };

  // Helper function to check if certificate should be shown (earned)
  const shouldShowCertificateButton = (enrollment: Enrollment): boolean => {
    const courseId = enrollment.course?._id;
    if (!courseId) return false;

    // Check if certificate exists
    if (certificateMap[courseId]) return true;

    // Also check if exam passed (certificate might be generated but not fetched)
    const examStatus = examStatusMap[courseId];
    if (examStatus?.examPassed) return true;

    // Check enrollment certificate flag
    if (enrollment.certificateIssued) return true;

    return false;
  };

  // Helper to get exam button text
  const getExamButtonText = (enrollment: Enrollment): string => {
    const courseId = enrollment.course?._id;
    if (!courseId) return "Take Exam";

    const examStatus = examStatusMap[courseId];
    const attemptsUsed = examStatus?.examAttempts || 0;
    const attemptsAllowed = examStatus?.examAttemptsAllowed || 3;

    if (attemptsUsed > 0) {
      return `Take Exam (Attempt ${attemptsUsed + 1}/${attemptsAllowed})`;
    }
    return "Take Certificate Exam";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  // Calculate stats
  const activeCourses = enrollments.filter((e) => e.status === "active").length;
  const completedCourses = enrollments.filter(
    (e) => e.status === "completed" || e.progress >= 100,
  ).length;
  const certificatesEarned =
    Object.values(certificateMap).filter(Boolean).length;
  const totalProgress =
    enrollments.length > 0
      ? Math.round(
          enrollments.reduce((acc, e) => acc + e.progress, 0) /
            enrollments.length,
        )
      : 0;

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              My Courses
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Continue your learning journey
          </p>
        </div>

        {/* Stats Summary */}
        {enrollments.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mb-5">
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <BookOpen className="w-4 h-4 mx-auto mb-0.5 text-orange-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {enrollments.length}
              </p>
              <p className="text-[7px] text-gray-500">Total</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <CheckCircle className="w-4 h-4 mx-auto mb-0.5 text-green-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {activeCourses}
              </p>
              <p className="text-[7px] text-gray-500">Active</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <Award className="w-4 h-4 mx-auto mb-0.5 text-amber-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {completedCourses}
              </p>
              <p className="text-[7px] text-gray-500">Completed</p>
            </div>
            <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
              <FileCheck className="w-4 h-4 mx-auto mb-0.5 text-emerald-500" />
              <p className="text-[10px] font-bold text-gray-900">
                {certificatesEarned}
              </p>
              <p className="text-[7px] text-gray-500">Certificates</p>
            </div>
          </div>
        )}

        {enrollments.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-gray-900">
              No courses yet
            </h3>
            <p className="text-[10px] text-gray-500 mb-4 max-w-xs mx-auto">
              Start learning by enrolling in a course
            </p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              Browse Courses
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <>
            {/* Courses Grid - Responsive */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {enrollments.map((enrollment) => {
                if (!enrollment.course) return null;
                const courseId = enrollment.course._id;
                const examStatus = examStatusMap[courseId];
                const certificateEarned = certificateMap[courseId];
                const isCompleted =
                  enrollment.progress >= 100 ||
                  enrollment.status === "completed";
                const showExam = shouldShowExamButton(enrollment);
                const showCertificate = shouldShowCertificateButton(enrollment);

                return (
                  <div
                    key={enrollment._id}
                    className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-all duration-200"
                  >
                    {/* Thumbnail */}
                    <div className="relative h-40 overflow-hidden bg-gray-100">
                      {enrollment.course?.thumbnail ? (
                        <img
                          src={enrollment.course.thumbnail}
                          alt={enrollment.course.title || "Course"}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-12 h-12 text-gray-400" />
                        </div>
                      )}

                      {/* Progress Badge */}
                      <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm px-2 py-0.5 rounded-full">
                        <span className="text-[9px] font-medium text-white">
                          {enrollment.progress || 0}% Complete
                        </span>
                      </div>

                      {/* Status Badge */}
                      {isCompleted && !certificateEarned && (
                        <div className="absolute top-2 left-2 bg-green-500 px-1.5 py-0.5 rounded-full">
                          <span className="text-[8px] font-bold text-white">
                            Completed
                          </span>
                        </div>
                      )}

                      {/* Certificate Earned Badge */}
                      {certificateEarned && (
                        <div className="absolute top-2 left-2 bg-amber-500 px-1.5 py-0.5 rounded-full">
                          <span className="text-[8px] font-bold text-white flex items-center gap-0.5">
                            <Award className="w-2.5 h-2.5" /> Certified
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="text-[11px] font-bold text-gray-900 mb-1 line-clamp-2 md:text-xs">
                        {enrollment.course?.title || "Untitled Course"}
                      </h3>

                      {/* Instructor */}
                      {enrollment.course?.instructorName && (
                        <p className="text-[9px] text-gray-500 mb-2">
                          by {enrollment.course.instructorName}
                        </p>
                      )}

                      {/* Progress Bar */}
                      <div className="mb-2">
                        <div className="flex justify-between text-[8px] text-gray-500 mb-0.5">
                          <span>Progress</span>
                          <span>{enrollment.progress || 0}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-600"
                            style={{ width: `${enrollment.progress || 0}%` }}
                          />
                        </div>
                      </div>

                      {/* Meta Info */}
                      <div className="flex items-center justify-between mb-3 text-[9px] text-gray-500">
                        <div className="flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" />
                          <span>
                            {enrollment.lastAccessedLesson
                              ? "In progress"
                              : enrollment.enrolledAt
                                ? new Date(
                                    enrollment.enrolledAt,
                                  ).toLocaleDateString()
                                : "Not started"}
                          </span>
                        </div>

                        {/* Exam Status Indicator */}
                        {examStatus?.hasCertificateExam &&
                          isCompleted &&
                          !certificateEarned &&
                          !examStatus?.examPassed && (
                            <div className="flex items-center gap-0.5 text-amber-600">
                              <Shield className="w-2.5 h-2.5" />
                              <span className="text-[7px] font-medium">
                                Exam Ready
                              </span>
                            </div>
                          )}

                        {certificateEarned && (
                          <div className="flex items-center gap-0.5 text-green-600">
                            <FileCheck className="w-2.5 h-2.5" />
                            <span className="text-[7px] font-medium">
                              Certified
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              handleContinue(enrollment.course?._id)
                            }
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
                          >
                            {enrollment.status === "completed"
                              ? "Review"
                              : "Continue"}
                            <ArrowRight className="w-3 h-3" />
                          </button>
                          {enrollment.course?._id && (
                            <Link
                              to={`/course/${enrollment.course._id}`}
                              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-[9px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                            >
                              Details
                            </Link>
                          )}
                        </div>

                        {/* Show "Take Certificate Exam" button when course completed but exam not taken */}
                        {showExam && (
                          <button
                            onClick={() =>
                              handleTakeCertificateExam(
                                enrollment.course?._id,
                                examStatus?.examId,
                              )
                            }
                            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-amber-600 to-orange-500 hover:shadow-md transition-all"
                          >
                            <Shield className="w-3 h-3" />
                            {getExamButtonText(enrollment)}
                          </button>
                        )}

                        {/* Show "View Certificate" button when certificate is earned */}
                        {showCertificate && (
                          <button
                            onClick={() =>
                              handleViewCertificate(enrollment.course?._id)
                            }
                            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[9px] font-medium text-white rounded-lg bg-gradient-to-r from-emerald-600 to-green-500 hover:shadow-md transition-all"
                          >
                            <FileCheck className="w-3 h-3" />
                            View Certificate
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Overall Progress Card */}
            <div className="mt-5 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[10px] text-gray-500">
                    Overall Learning Progress
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {totalProgress}% Complete
                  </p>
                </div>
                <div className="flex-1 max-w-xs">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-600"
                      style={{ width: `${totalProgress}%` }}
                    />
                  </div>
                </div>
                <Link
                  to="/courses"
                  className="text-[10px] text-orange-600 hover:underline flex items-center gap-0.5"
                >
                  Find more courses
                  <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentCourses;
